import {useCallback, useRef} from 'react';

import type {RichTextEditorHandle} from '../components/RichTextEditor';
import {caretOffsetAtPoint} from '../lib/caret-from-point';
import type {RichText} from '../lib/rich-text';

type CellAddress = {row: number; col: number};

/**
 * Where focus goes in a table: its wrapper, or a cell's editor.
 *
 * Two things race a gesture that opens the editor, and this owns both:
 *
 * - **The wrapper focus.** Pressing a cell, closing an editor or tabbing out of
 *   the last cell focuses the table wrapper a frame later, so the keyboard
 *   reaches the table (Delete removes the selected element, not the whole
 *   table). If an editor opens in between, that focus would land after the
 *   editor mounts, blur it, and the blur commits and closes the edit. Every
 *   wrapper focus goes through `focusWrapper`, so opening can always cancel it.
 * - **The caret.** An editor that mounts focused puts its caret at the end.
 *   The author pointed at a character; opening reads that offset from the drawn
 *   text BEFORE it is swapped out, and `placeCaret` applies it once on mount.
 */
export function useCellEditorFocus({
  tableWrapperRef,
  setEditingCell,
  setDraft,
}: {
  tableWrapperRef: React.RefObject<HTMLElement | null>;
  setEditingCell: (cell: CellAddress) => void;
  setDraft: (text: RichText) => void;
}) {
  const focusRafRef = useRef<number | null>(null);
  const pendingCaretRef = useRef<number | null>(null);

  const cancelWrapperFocus = useCallback(() => {
    if (focusRafRef.current === null) return;
    cancelAnimationFrame(focusRafRef.current);
    focusRafRef.current = null;
  }, []);

  /** Focus the wrapper next frame, unless an editor opens first. */
  const focusWrapper = useCallback(() => {
    cancelWrapperFocus();
    focusRafRef.current = requestAnimationFrame(() => {
      focusRafRef.current = null;
      tableWrapperRef.current?.focus({preventScroll: true});
    });
  }, [cancelWrapperFocus, tableWrapperRef]);

  /** Open `cell` for editing with `text`, caret where the pointer of `e` is. */
  const openEditor = useCallback(
    (cell: CellAddress, text: RichText, e: React.MouseEvent) => {
      cancelWrapperFocus();
      pendingCaretRef.current = caretOffsetAtPoint(
        e.clientX,
        e.clientY,
        e.currentTarget,
        text.text,
      );
      setEditingCell(cell);
      setDraft(text);
    },
    [cancelWrapperFocus, setEditingCell, setDraft],
  );

  /** Ref for the cell's editor; null pending caret leaves the editor's own. */
  const placeCaret = useCallback((editor: RichTextEditorHandle | null) => {
    if (!editor || pendingCaretRef.current === null) return;
    const at = pendingCaretRef.current;
    pendingCaretRef.current = null;
    editor.setCaret(at);
  }, []);

  return {openEditor, placeCaret, focusWrapper};
}
